import { useCallback, useEffect, useRef } from 'react';
import { INTRO_COMMISSION_ID } from '@sorrem/shared';
import type { UIGameState } from '@sorrem-client/types';
import { GuidedTour } from '@sorrem-client/ui/features/GuidedTour';
import { hasOtherRosterMembers } from '@sorrem-client/ui/features/Hud';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { useGameSelector } from '@sorrem-client/ui/hooks';

/** Whether the run is the intro Crucible, played alone, and under way. */
const isIntroUnderWay = (state: UIGameState): boolean => state.commission?.commissionId === INTRO_COMMISSION_ID
    && state.crucibleSessionState?.phase === 'playing'
    && !hasOtherRosterMembers(state.crucibleSessionState.roster)
    && state.runResult === null;

/**
 * The intro Crucible's HUD tour (#234), once the run is under way. While it
 * is up the Crucible holds its enemies (`requestTourHold`), so the player
 * never fights with it on screen; leaving the run with it up lets them go.
 */
export function RunHudTour(): JSX.Element {
    const { bridge, requestTourHold } = useGameplay();
    const isIntroRunning = useGameSelector(bridge, isIntroUnderWay);
    const isHoldingRef = useRef(false);

    const handleRunningChange = useCallback((isRunning: boolean): void => {
        if (isRunning !== isHoldingRef.current) {
            isHoldingRef.current = isRunning;
            requestTourHold(isRunning);
        }
    }, [requestTourHold]);

    useEffect(() => (): void => {
        if (isHoldingRef.current) {
            isHoldingRef.current = false;
            requestTourHold(false);
        }
    }, [requestTourHold]);

    return <GuidedTour tourId='run-hud' isEnabled={isIntroRunning} onRunningChange={handleRunningChange} />;
}
